<template>
  <BaseModal
    v-bind="modalProps"
    :content-class="
      twMerge(
        'tw:h-full tw:flex tw:flex-col tw:flex-nowrap tw:overflow-hidden',
        $attrs.class as string
      )
    "
    persistent
    @close="emit('close')"
  >
    <template #header>
      <slot name="header">
        <div
          v-if="headerTitle"
          class="tw:flex tw:items-center tw:gap-2 tw:text-base tw:font-bold"
          data-testid="header"
        >
          {{ headerTitle }}
        </div>
      </slot>
    </template>

    <div class="tw:flex tw:h-full">
      <div
        class="tw:flex tw:flex-col tw:flex-shrink-0 tw:px-4 tw:py-2 tw:h-full"
        data-testid="side-bar"
        :style="{ width: sidebarWidth }"
      >
        <div :class="twMerge('tw:mb-2 tw:mt-[1px] tw:font-bold', headingClass)">
          <slot name="heading" />
        </div>
        <slot name="aside" />
      </div>
      <slot name="default" />
    </div>

    <template #buttons="{ close }">
      <div class="tw:flex tw:w-full tw:gap-2">
        <Transition name="fade">
          <template v-if="alert">
            <BaseAlertMessage
              v-if="Boolean(alert?.message)"
              class="tw:p-0! tw:px-1!"
              :type="alert.type"
            >
              {{ alert.message }}
            </BaseAlertMessage>
          </template>
        </Transition>
        <slot
          :close="close"
          name="buttons"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, useAttrs } from "vue";

import { type AlertType, BaseAlertMessage } from "shared/components/base";
import BaseModal from "shared/components/core/modals/BaseModal.vue";
import type { ModalProps } from "shared/components/core/modals/types";

export type Alert = {
  message: string;
  type: AlertType;
};

type ModalTwoColumnProps = {
  alert?: Alert;
  headerTitle?: string;
  headingClass?: string;
  sidebarWidth?: string;
};

type ModalTwoColumnPropsCombined = ModalProps & ModalTwoColumnProps;

const props = withDefaults(defineProps<ModalTwoColumnPropsCombined>(), {
  alert: undefined,
  headerTitle: undefined,
  headingClass: undefined,
  sidebarWidth: "246px",
});

defineOptions({
  name: "BaseModalTwoColumn",
  inheritAttrs: false,
});

const emit = defineEmits<{ close: [] }>();

const attrs = useAttrs();

const modalProps = computed(() => {
  const { alert, headerTitle, sidebarWidth, ...modalSpecificProps } = props;

  // inheritAttrs is false to allow manual handling of the class attr (merged in content-class).
  // However, we must pass other attrs (especially style with z-index from ModalsContainer)
  // to the BaseModal component to ensure proper z-index
  const { class: className, ...attrsWithoutClass } = attrs;

  return { ...modalSpecificProps, ...attrsWithoutClass };
});
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/fade";
</style>
